import React, { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ClipboardList, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { SoftBackground } from '@/components/SoftBackground';
import { Logo } from '@/components/Logo';
import { WizardSidebar } from '@/components/wizard/WizardSidebar';
import { WizardField } from '@/components/wizard/WizardField';
import { ReviewPanel } from '@/components/ReviewPanel';
import { SuccessScreen } from '@/components/SuccessScreen';
import { Turnstile } from '@/components/Turnstile';
import { TurnstileModal } from '@/components/TurnstileModal';
import { Button } from '@/components/ui/button';
import { FLOW, SECTION_ORDER } from '@/data/flow';
import { saveApplication } from '@/lib/storage';
import { API } from '@/lib/api';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const sanitizeAnswers = (a) => ({
  ...a,
  photo: a.photo instanceof File ? { name: a.photo.name, size: a.photo.size } : a.photo,
});

const defaultFor = (step) => {
  if (step.type === 'multi-skill') return [];
  if (step.type === 'links') return [];
  if (step.type === 'skill-test') return [];
  if (step.type === 'social') return { instagram: '', linkedin: '', github: '', twitter: '' };
  if (step.type === 'education') return { level: '', institution: '', major: '', year: '' };
  if (step.type === 'photo') return null;
  return '';
};

const isEmptyValue = (step, v) => {
  if (step.type === 'multi-skill' || step.type === 'links') return !v || v.length === 0;
  if (step.type === 'skill-test') return !v || v.length === 0 || v.every((x) => !x.answer || !x.answer.trim());
  if (step.type === 'social') return !v || Object.values(v).every((x) => !x || x.trim() === '');
  if (step.type === 'education') return !v || (!v.level && !v.institution && !v.major);
  if (step.type === 'salary') return false;
  if (step.type === 'photo') return !v;
  return !v || (typeof v === 'string' && v.trim() === '');
};

const validateStep = (step, v) => {
  if (step.optional && isEmptyValue(step, v)) return null;
  switch (step.type) {
    case 'text': case 'email': case 'tel': case 'link': case 'longtext': {
      const val = (v || '').trim();
      if (step.validate) return step.validate(val);
      return val ? null : 'Bagian ini wajib diisi, ya.';
    }
    case 'education':
      if (!v?.level) return 'Pilih jenjang pendidikanmu.';
      if (!v?.institution || v.institution.trim().length < 2) return 'Isi nama sekolah / kampusmu.';
      if (!v?.major || v.major.trim().length < 2) return 'Isi jurusanmu.';
      return null;
    case 'single-track':
    case 'single-duration':
      return v ? null : 'Pilih salah satu dulu, ya.';
    case 'multi-skill':
      return v && v.length > 0 ? null : 'Pilih minimal satu skill.';
    case 'links':
      if (step.validate) return step.validate(v);
      return null;
    case 'skill-test':
      if (!v || v.length === 0 || v.some((x) => !x.answer || x.answer.trim().length < 2)) {
        return 'Jawab semua pertanyaannya dulu, ya. Boleh singkat.';
      }
      return null;
    default:
      return null;
  }
};

// ------------------------------------------------------------------ gate screen
function GateScreen({ siteKey, onPass }) {
  const [token, setToken] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const handleVerify = (t) => {
    setToken(t);
  };

  const handleContinue = () => {
    if (token) onPass(token);
  };

  // If Turnstile unavailable (network block, etc), let user through anyway
  const handleError = () => {
    setUnavailable(true);
    onPass(null);
  };

  return (
    <motion.div
      key="gate"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      className="w-full"
    >
      <div className="mx-auto max-w-md rounded-3xl border border-border bg-card p-8 shadow-elegant text-center">
        <div className="mb-5 flex justify-center">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <ShieldCheck className="h-7 w-7 text-primary" />
          </span>
        </div>
        <h2 className="font-display text-2xl font-bold text-foreground">Verifikasi dulu, ya</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Selesaikan verifikasi berikut untuk memastikan kamu bukan bot sebelum mengisi formulir.
        </p>
        <div className="mt-6 flex justify-center">
          {!unavailable && (
            <Turnstile siteKey={siteKey} onVerify={handleVerify} onError={handleError} />
          )}
        </div>
        <div className="mt-6">
          <Button
            variant="brand"
            size="lg"
            className="w-full"
            disabled={!token && !unavailable}
            onClick={handleContinue}
          >
            Mulai Mengisi Formulir <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        {unavailable && (
          <p className="mt-3 text-xs text-muted-foreground">
            Verifikasi tidak tersedia. Kamu tetap bisa melanjutkan.
          </p>
        )}
      </div>
    </motion.div>
  );
}

export default function WizardPage() {
  // mode: wizard | review | success
  const [mode, setMode] = useState('wizard');
  const [stepIndex, setStepIndex] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(1);
  const [error, setError] = useState(null);
  const [returnToReview, setReturnToReview] = useState(false);
  const [record, setRecord] = useState(null);
  const [revised, setRevised] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileUnavailable, setTurnstileUnavailable] = useState(false);
  const [showTurnstileModal, setShowTurnstileModal] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [startTime, setStartTime] = useState(() => Date.now());

  const step = FLOW[stepIndex];
  const rawValue = answers[step.key];

  const progress = useMemo(() => {
    if (mode === 'review' || mode === 'success') return 100;
    return Math.round((Object.keys(answers).length / FLOW.length) * 100);
  }, [answers, mode]);

  const sections = useMemo(() => SECTION_ORDER.map((name) => {
    const firstIndex = FLOW.findIndex((s) => s.section === name);
    const lastIndex = FLOW.reduce((acc, s, i) => (s.section === name ? i : acc), -1);
    let status = 'todo';
    if (mode === 'review' || mode === 'success') status = 'done';
    else if (stepIndex > lastIndex) status = 'done';
    else if (stepIndex >= firstIndex && stepIndex <= lastIndex) status = 'active';
    return { name, firstIndex, lastIndex, status, clickable: firstIndex <= maxVisited && mode === 'wizard' };
  }), [stepIndex, maxVisited, mode]);

  const setValue = useCallback((v) => {
    setAnswers((a) => ({ ...a, [step.key]: v }));
    setError(null);
  }, [step.key]);

  const goNext = useCallback((explicit) => {
    const current = (step.key in answers) ? answers[step.key] : defaultFor(step);
    const v = explicit !== undefined ? explicit : current;
    const err = validateStep(step, v);
    if (err) { setError(err); return; }
    setAnswers((a) => ({ ...a, [step.key]: v }));
    setError(null);

    if (returnToReview) {
      setReturnToReview(false);
      setMode('review');
      return;
    }
    const ni = stepIndex + 1;
    if (ni >= FLOW.length) {
      setMode('review');
    } else {
      setDirection(1);
      setStepIndex(ni);
      setMaxVisited((m) => Math.max(m, ni));
    }
  }, [answers, step, stepIndex, returnToReview]);

  const goBack = useCallback(() => {
    setError(null);
    if (returnToReview) { setReturnToReview(false); setMode('review'); return; }
    if (stepIndex === 0) return;
    setDirection(-1);
    setStepIndex((i) => i - 1);
  }, [stepIndex, returnToReview]);

  const jumpTo = useCallback((idx) => {
    setError(null);
    setReturnToReview(false);
    setDirection(idx > stepIndex ? 1 : -1);
    setStepIndex(idx);
    setMode('wizard');
  }, [stepIndex]);

  const editFromReview = useCallback((key) => {
    const idx = FLOW.findIndex((s) => s.key === key);
    setError(null);
    setReturnToReview(true);
    setDirection(1);
    setStepIndex(idx);
    setMaxVisited((m) => Math.max(m, idx));
    setMode('wizard');
  }, []);

  const handleGatePass = useCallback((token) => {
    if (token) {
      setTurnstileToken(token);
    } else {
      setTurnstileUnavailable(true);
    }
    setStartTime(Date.now());
    setMode('wizard');
  }, []);

  const submit = useCallback(async (token) => {
    // token diterima dari TurnstileModal setelah verifikasi
    setSubmitting(true);
    try {
      const { photo, ...answersForPayload } = answers;
      // salary tidak ditampilkan ke user, default fleksibel (null)
      answersForPayload.salary = answersForPayload.salary ?? null;
      const form = new FormData();
      form.append('payload', JSON.stringify({
        answers: answersForPayload,
        existingId: record?.id || undefined,
        meta: {
          honeypot,
          elapsedMs: Date.now() - startTime,
          userAgent: navigator.userAgent,
        },
      }));
      if (token) form.append('turnstile_token', token);
      if (photo instanceof File) form.append('photo', photo);

      const res = await fetch(`${API}/applications`, { method: 'POST', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Gagal mengirim lamaran. Coba lagi.');
      }

      saveApplication(sanitizeAnswers(answers), record?.id || null);
      setRecord({ id: data.id, photoUrl: data.photoUrl });
      setRevised(!!record);
      setMode('success');
    } catch (e) {
      toast.error(e.message || 'Terjadi kesalahan jaringan. Coba lagi.');
    } finally {
      setSubmitting(false);
    }
  }, [answers, honeypot, record, startTime]);

  // Saat user klik Submit di ReviewPanel:
  // Kalau Turnstile dikonfigurasi → buka modal dulu
  // Kalau tidak → langsung submit
  const handleSubmitClick = useCallback(() => {
    if (TURNSTILE_SITE_KEY) {
      setShowTurnstileModal(true);
    } else {
      submit(null);
    }
  }, [submit]);

  // Dipanggil setelah user berhasil verifikasi di modal
  const handleTurnstileVerified = useCallback((token) => {
    setShowTurnstileModal(false);
    setTurnstileToken(token);
    submit(token);
  }, [submit]);

  // Dipanggil kalau Turnstile error/unavailable → submit tanpa token
  const handleTurnstileError = useCallback(() => {
    setShowTurnstileModal(false);
    setTurnstileUnavailable(true);
    submit(null);
  }, [submit]);

  const restart = useCallback(() => {
    setAnswers({});
    setStepIndex(0);
    setMaxVisited(0);
    setError(null);
    setReturnToReview(false);
    setRecord(null);
    setRevised(false);
    setTurnstileToken(null);
    setTurnstileUnavailable(false);
    setHoneypot('');
    setStartTime(Date.now());
    setMode('wizard');
  }, []);

  const isLast = stepIndex === FLOW.length - 1;
  const nextLabel = returnToReview ? 'Simpan & kembali' : isLast ? 'Lihat ringkasan' : 'Lanjut';

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 36 : -36 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -36 : 36 }),
  };

  return (
    <div className="relative min-h-screen">
      <SoftBackground />
      {/* honeypot — hidden from real users, bots tend to fill it */}
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-6 sm:px-6 lg:py-9">
        <header className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-sm font-medium text-muted-foreground">Program Magang</span>
          </div>
          <span className="hidden text-sm text-muted-foreground sm:block">Surabaya</span>
        </header>

        <main className="flex flex-1 items-start">
          <AnimatePresence mode="wait">
            {mode === 'wizard' && (
              <motion.div
                key="wizard"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="grid w-full overflow-hidden rounded-3xl border border-border bg-card shadow-elegant lg:grid-cols-[260px_1fr]"
              >
                <WizardSidebar sections={sections} onJump={jumpTo} progress={progress} />

                <div className="flex min-h-[600px] min-w-0 flex-col p-6 sm:p-10">
                  {/* mobile/contextual progress header */}
                  <div className="mb-7">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold uppercase tracking-[0.14em] text-primary">{step.section}</span>
                      <span className="text-muted-foreground">Langkah {stepIndex + 1} / {FLOW.length}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
                      <div className="h-full rounded-full bg-gradient-brand transition-all duration-500" style={{ width: `${Math.round(((stepIndex + 1) / FLOW.length) * 100)}%` }} />
                    </div>
                  </div>

                  {/* question */}
                  <div className="flex-1">
                    <AnimatePresence mode="wait" custom={direction}>
                      <motion.div
                        key={stepIndex}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <h2 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-3xl">
                          {step.title}
                        </h2>
                        {step.hint && <p className="mt-2 text-sm text-muted-foreground sm:text-base">{step.hint}</p>}

                        <div className="mt-7">
                          <WizardField
                            step={step}
                            value={rawValue}
                            answers={answers}
                            onChange={setValue}
                            onEnter={() => goNext()}
                            onAutoAdvance={(v) => goNext(v)}
                          />
                        </div>

                        {error && (
                          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 text-sm font-medium text-destructive">
                            {error}
                          </motion.p>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* footer nav */}
                  <div className="mt-8 flex items-center justify-between gap-3 border-t border-border pt-5">
                    <Button
                      onClick={goBack}
                      variant="ghost"
                      disabled={stepIndex === 0 && !returnToReview}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-0"
                    >
                      <ArrowLeft className="h-4 w-4" /> Kembali
                    </Button>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => goNext()} variant="brand" size="lg">
                        {nextLabel}
                        {isLast && !returnToReview ? <ClipboardList className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {mode === 'review' && (
              <motion.div key="review" className="w-full" exit={{ opacity: 0 }}>
                <ReviewPanel
                  answers={answers}
                  onEdit={editFromReview}
                  onSubmit={handleSubmitClick}
                  onBack={() => jumpTo(FLOW.length - 1)}
                  submitLabel={record ? 'Simpan revisi' : 'Kirim Lamaran'}
                  submitting={submitting}
                />
              </motion.div>
            )}

            {mode === 'success' && (
              <motion.div key="success" className="w-full" exit={{ opacity: 0 }}>
                <SuccessScreen
                  record={record}
                  answers={answers}
                  revised={revised}
                  onRevise={() => setMode('review')}
                  onRestart={restart}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Turnstile Modal — muncul saat Submit diklik di ReviewPanel */}
      {TURNSTILE_SITE_KEY && (
        <TurnstileModal
          open={showTurnstileModal}
          siteKey={TURNSTILE_SITE_KEY}
          onVerified={handleTurnstileVerified}
          onClose={() => setShowTurnstileModal(false)}
          onError={handleTurnstileError}
        />
      )}
    </div>
  );
}
